import React, { ReactNode, useCallback } from "react"
import { useEffect, useRef, useState } from "react"

type SideMenuDropdownButtonProps = {
    label: string
    subLabel?: string
    tooltipLabel?: string
    options: ReactNode[]
    icon: React.ReactNode
    sideMenuOpen: boolean
    isMobile: boolean
    className?: string
}
const SideMenuDropdownButton = ({ label, subLabel, tooltipLabel, options, icon, sideMenuOpen, isMobile, className }: SideMenuDropdownButtonProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const selectRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom')

    const updatePlacement = useCallback(() => {
        const triggerRect = selectRef.current?.getBoundingClientRect()
        const listHeight = listRef.current?.offsetHeight ?? 0

        if (!triggerRect) return;

        const spaceBelow = window.innerHeight - triggerRect.bottom
        const needsFlip = spaceBelow < listHeight + 8

        setPlacement(needsFlip ? 'top' : 'bottom')
    }, [])

    useEffect(() => {
        if (isOpen) {
            updatePlacement() // measure once when opening
            window.addEventListener('resize', updatePlacement) // keep it responsive
            return () => window.removeEventListener('resize', updatePlacement)
        }
    }, [isOpen, updatePlacement])

    const handleToggleDropdown = () => setIsOpen(!isOpen)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return <div className="relative" ref={selectRef}>
        <div className={`flex items-center justify-between ${sideMenuOpen ? 'w-full' : 'w-fit'} px-2 h-12 rounded-lg
                hover:cursor-pointer hover:dark:bg-darkMode-layer2color transition-colors duration-200
                focus:outline-none ${className}`} onClick={handleToggleDropdown} >


            <div className="flex items-center">
                <span className="relative flex-shrink-0">

                    <span className="relative flex items-center justify-center rounded-md w-fit">
                        {icon}
                    </span>
                </span>
                {(sideMenuOpen || isMobile) && (
                    <div className={`flex flex-col text-left ${isMobile ? "max-w-[200px]" : "max-w-[150px]"} ml-3`}>
                        <span className="text-[14px] truncate">{label}</span>
                        {subLabel && (
                            <span className="text-[12px] dark:text-darkMode-secondaryText truncate">{subLabel}</span>
                        )}
                    </div>

                )}
            </div>

            {sideMenuOpen && (
                <svg width="18" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {/* Chevron Up */}
                    <polyline points="5 10 9 6 13 10" />
                    {/* Chevron Down */}
                    <polyline points="5 13 9 17 13 13" />
                </svg>
            )}

        </div>
        {isOpen && (
            <ul
                ref={listRef}
                className={`
            absolute z-[6000] min-w-[220px] max-w-[400px] rounded-lg overflow-y-auto text-[14px]
            dark:bg-darkMode-layer2color border border-darkMode-layer3border
            shadow-lg shadow-[rgba(0,0,0,0.8)]
            ${isMobile
                        ?
                        placement === 'bottom' ? 'top-full w-full' : 'bottom-full w-full'
                        :
                        placement === 'bottom' ? 'top-0 left-full' : 'bottom-0 left-full'
                    }
            max-h-[calc(100dvh-4rem)] lg:max-h-[calc(100dvh-8rem)]
          `}
            >
                {tooltipLabel && (
                    <li className="px-4 pt-2 text-[12px] dark:text-darkMode-secondaryText pointer-events-none">
                        {tooltipLabel}
                    </li>
                )}
                {options.map((option, index) => (
                    <li key={index} onClick={() => setIsOpen(false)}>
                        {option}
                    </li>
                ))}
            </ul>
        )}
    </div>
}

export default SideMenuDropdownButton
